import React, { useEffect } from "react";
import { X, MessageCircle, Mail, Phone } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Modal that lets a visitor choose how to contact the entrepreneur about a specific product.
 * Tracks the click on each channel and then opens the corresponding link.
 *
 * Props:
 *   - product:        product doc (or null to hide)
 *   - entrepreneur:   the entrepreneur profile (for whatsapp, email, phone)
 *   - onClose:        () => void
 */
const ContactProductModal = ({ product, entrepreneur, onClose }) => {
  const { t } = useI18n();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    if (product) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [product, onClose]);

  if (!product || !entrepreneur) return null;

  const waNumber = (entrepreneur.whatsapp || "").replace(/\D/g, "");
  const phone = entrepreneur.phone || "";
  const email = entrepreneur.email || "";
  const presetText = encodeURIComponent(
    `${t.catalog.contactBtn}: ${product.title} — ${entrepreneur.business_name || ""}`
  );

  const channels = [
    waNumber && {
      key: "whatsapp",
      label: t.catalog.contactWa,
      Icon: MessageCircle,
      url: `https://wa.me/${waNumber}?text=${presetText}`,
      color: "bg-[#25D366] hover:bg-[#1ebe57] text-white",
    },
    email && {
      key: "email",
      label: t.catalog.contactEmail,
      Icon: Mail,
      url: `mailto:${email}?subject=${encodeURIComponent(product.title)}&body=${presetText}`,
      color: "bg-teal hover:bg-teal-deep text-white",
    },
    phone && {
      key: "phone",
      label: t.catalog.contactPhone,
      Icon: Phone,
      url: `tel:${phone.replace(/\s+/g, "")}`,
      color: "bg-orange hover:bg-orange/90 text-white",
    },
  ].filter(Boolean);

  const handleClick = (kind, url) => {
    // Fire-and-forget click tracking
    api.post(`/products/${product.id}/contact-click?kind=${kind}`).catch(() => {});
    window.open(url, kind === "phone" || kind === "email" ? "_self" : "_blank", "noopener,noreferrer");
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="product-contact-modal"
    >
      <div
        className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 md:p-7 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center"
            aria-label="close"
            data-testid="contact-modal-close"
          >
            <X size={16} />
          </button>
          <p className="eyebrow text-orange">{t.catalog.contactBtn}</p>
          <h3 className="font-display text-2xl text-teal-deep mt-1 leading-tight pr-8">
            {t.catalog.contactTitle.replace("{biz}", entrepreneur.business_name || "")}
          </h3>
          <p className="text-sm text-teal-soft mt-2">
            {t.catalog.contactSubtitle.replace("{product}", product.title)}
          </p>
        </div>

        <div className="p-6 md:p-7">
          {channels.length === 0 ? (
            <p className="text-sm text-teal-soft italic">{t.catalog.noContact}</p>
          ) : (
            <div className="space-y-3">
              {channels.map(({ key, label, Icon, url, color }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleClick(key, url)}
                  className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-semibold transition-all ${color}`}
                  data-testid={`contact-${key}`}
                >
                  <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Icon size={18} />
                  </span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactProductModal;
